/**
 * Authentication Service
 * Handles authentication with backend API
 * Replaces Supabase Auth during migration
 */

import { getBackendUrl } from '@/lib/api-helpers';

const TOKEN_KEY = 'runara_auth_token';

export interface AuthUser {
  id: string;
  username: string | null;
  email: string | null;
}

export interface AuthProfile {
  id: string;
  username: string | null;
  player_pda: string | null;
  [key: string]: any;
}

export interface AuthSession {
  user: AuthUser;
  profile: AuthProfile;
}

export class AuthService {
  private static getBackendUrl(): string {
    return getBackendUrl();
  }

  /**
   * Sign in with username and password
   */
  static async signIn(username: string, password: string): Promise<AuthSession> {
    const backendUrl = this.getBackendUrl();
    const response = await fetch(`${backendUrl}/api/auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Sign in failed' }));
      throw new Error(error.error || 'Failed to sign in');
    }

    const data = await response.json();

    // Store token
    if (data.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
    }

    return {
      user: data.user,
      profile: data.profile,
    };
  }

  /**
   * Sign up new user
   */
  static async signUp(username: string, password: string): Promise<AuthSession> {
    const backendUrl = this.getBackendUrl();
    const response = await fetch(`${backendUrl}/api/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Sign up failed' }));
      throw new Error(error.error || 'Failed to create account');
    }

    const data = await response.json();

    // Store token
    if (data.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
    }

    return {
      user: data.user,
      profile: data.profile,
    };
  }

  /**
   * Sign in with a Solana wallet signature (SIWS).
   * If no account exists for the wallet, one is auto-created.
   */
  static async walletSignIn(
    walletAddress: string,
    signature: string,
    message: string,
  ): Promise<AuthSession> {
    const backendUrl = this.getBackendUrl();
    const response = await fetch(`${backendUrl}/api/auth/wallet-signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ walletAddress, signature, message }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Wallet sign in failed' }));
      throw new Error(error.error || 'Failed to sign in with wallet');
    }

    const data = await response.json();

    if (data.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
    }

    return {
      user: data.user,
      profile: data.profile,
    };
  }

  /**
   * Admin bypass: sign in with password to bypass waitlist (for dev/admin access)
   */
  static async adminBypass(password: string): Promise<AuthSession> {
    const backendUrl = this.getBackendUrl();
    const response = await fetch(`${backendUrl}/api/auth/admin-bypass`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Admin bypass failed' }));
      throw new Error(error.error || 'Invalid password');
    }

    const data = await response.json();

    if (data.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
    }

    return {
      user: data.user,
      profile: data.profile,
    };
  }

  /**
   * Get current session
   */
  static async getSession(): Promise<AuthSession | null> {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    const backendUrl = this.getBackendUrl();
    let response: Response;
    try {
      response = await fetch(`${backendUrl}/api/auth/session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch {
      // Network/CORS error - don't clear token; it may still be valid
      return null;
    }

    if (response.status === 401) {
      // Token explicitly rejected as invalid
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    if (!response.ok) {
      // 5xx or other - server error, don't clear token
      return null;
    }

    const data = await response.json();
    return {
      user: data.user,
      profile: data.profile,
    };
  }

  /**
   * Sign out
   */
  static async signOut(): Promise<void> {
    const token = this.getToken();
    if (token) {
      const backendUrl = this.getBackendUrl();
      try {
        await fetch(`${backendUrl}/api/auth/signout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        // Ignore errors - token removal is client-side
      }
    }
    localStorage.removeItem(TOKEN_KEY);
  }

  /**
   * Get stored token
   */
  static getToken(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Check if user is authenticated
   */
  static async isAuthenticated(): Promise<boolean> {
    const session = await this.getSession();
    return session !== null;
  }
}
